/**
 * AttachmentsTab - File attachments for an annotation.
 */

import { useRef, useState } from 'react'
import { format } from 'date-fns'
import {
  Paperclip,
  Upload,
  Download,
  Trash2,
  File,
  FileText,
  Image,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
// Input not currently used but may be needed for search
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useAttachments, useUploadAttachment, useDeleteAttachment } from '@/hooks/useAnnotations'
import { downloadAttachment } from '@/lib/annotationApi'
import type { Attachment } from '@/types/annotation'

interface AttachmentsTabProps {
  annotationId: number
  studyId: string
}

// Maximum file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024
// Allowed file types
const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg']

export function AttachmentsTab({ annotationId, studyId }: AttachmentsTabProps) {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // State
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)
  const [downloading, setDownloading] = useState<number | null>(null)

  // Fetch attachments
  const { data, isLoading, error } = useAttachments(studyId, annotationId)

  // Mutations
  const uploadAttachment = useUploadAttachment(studyId, annotationId)
  const deleteAttachment = useDeleteAttachment(studyId, annotationId)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: 'Invalid File Type',
        description: `Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed`,
        variant: 'destructive',
      })
      return
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: 'File Too Large',
        description: 'Maximum file size is 10MB',
        variant: 'destructive',
      })
      return
    }

    try {
      await uploadAttachment.mutateAsync(file)
    } catch {
      // Error handled by hook
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDownload = async (attachment: Attachment) => {
    setDownloading(attachment.id)
    try {
      const blob = await downloadAttachment(studyId, annotationId, attachment.id)

      // Create download link
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast({
        title: 'Download Complete',
        description: `${attachment.filename} downloaded successfully`,
      })
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: error instanceof Error ? error.message : 'Failed to download file',
        variant: 'destructive',
      })
    } finally {
      setDownloading(null)
    }
  }

  const handleDelete = async () => {
    if (deleteTarget === null) return

    try {
      await deleteAttachment.mutateAsync(deleteTarget)
      setDeleteTarget(null)
    } catch {
      // Error handled by hook
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="h-8 w-8 text-blue-500" />
    }
    if (mimeType === 'application/pdf') {
      return <FileText className="h-8 w-8 text-red-500" />
    }
    return <File className="h-8 w-8 text-gray-500" />
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Failed to load attachments
      </div>
    )
  }

  const attachments = data?.attachments || []

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      <div className={`border-2 border-dashed rounded-lg p-4 text-center ${uploadAttachment.isPending ? 'bg-muted/50' : ''}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploadAttachment.isPending}
        />
        {uploadAttachment.isPending ? (
          <>
            <Loader2 className="h-8 w-8 mx-auto text-primary mb-2 animate-spin" />
            <p className="text-sm font-medium text-primary mb-2">
              Uploading file...
            </p>
            <p className="text-xs text-muted-foreground">
              Please wait while your file is being uploaded
            </p>
          </>
        ) : (
          <>
            <Paperclip className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-2">
              Drop files here or click to upload
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              Select File
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              PDF, PNG, JPG up to 10MB
            </p>
          </>
        )}
      </div>

      {/* Attachments List */}
      {attachments.length === 0 ? (
        <div className="text-center py-8">
          <Paperclip className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No attachments yet</p>
          <p className="text-sm text-muted-foreground">Upload files to attach to this issue</p>
        </div>
      ) : (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              {getFileIcon(attachment.mimeType)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{attachment.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(attachment.fileSize)} • Uploaded by {attachment.uploader?.fullName || 'Unknown'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(attachment.uploadedAt), 'PPp')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(attachment)}
                  disabled={downloading === attachment.id}
                >
                  {downloading === attachment.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(attachment.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this attachment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAttachment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
